// Learning (F02, ADR-0014): candidates from `learn.list` (partners only) with their state; a candidate's evidence, validation
// (layers, before/after on recorded cases, tests) and plain-language explanation are the engine's `learn.show`, asked through
// the inbox. Approve / reject / apply / roll back are the signed-in partner's acts, each after the explanation is on screen.
// The firm's metrics (definitions, trend, applied versions) come from `learn.metrics`.
import { useState } from "react";
import { Card, PageHeader, StatusPill } from "../../components";
import { fmtStamp, titleCase } from "../../lib/format";
import { ActButton, ApprovalPanel, Computed, ModelGate, ReadView, useActs, useEngineRead, useFirmModels } from "./common";

type Doc = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

export function LearningPage() {
  const ms = useFirmModels(["learning", "learning_metrics"]);
  const [act, drawer] = useActs();
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <PageHeader title="Learning" subtitle="What the agents could learn from the firm's corrections, validated before and after on recorded cases. Only a partner approves, applies or rolls back." />
      {ms.error && <p role="alert" className="text-sm text-blocked">{ms.error}</p>}
      {ms.data && (
        <>
          <ModelGate<Doc> kind="learning" m={ms.data.learning} label="Learning candidates">
            {(d, m) => {
              const cs: Doc[] = d.candidates ?? [];
              return (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
                  <Card title="Candidates" action={<Computed m={m} />} bodyClass="p-0">
                    {cs.length === 0 ? <p className="p-4 text-sm text-muted">No candidate. Candidates are proposed from observations of corrections (learn.propose).</p> : (
                      <ul className="divide-y divide-line">
                        {cs.map((c) => (
                          <li key={c.id}>
                            <button type="button" aria-pressed={open === c.id} onClick={() => setOpen(c.id)} className={`w-full px-3 py-2 text-left text-sm ${open === c.id ? "bg-brand/10" : "hover:bg-sunken"}`}>
                              <span className="flex flex-wrap items-center gap-2"><StatusPill state={c.state === "awaiting_approval" ? "waiting" : c.state} label={titleCase(String(c.state))} /><span className="font-mono text-xs">#{c.id}</span>{c.agent && <span className="text-xs text-agent">✦ {c.agent}</span>}</span>
                              <span className="mt-0.5 block">{titleCase(String(c.kind))} · {c.scope}</span>
                              <span className="block text-xs text-muted">metric {c.metric} · proposed by {c.proposed_by} {fmtStamp(c.proposed_at)}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </Card>
                  <div>{open !== null ? <CandidateDetail id={open} act={act} /> : <p className="text-sm text-muted">Choose a candidate to see its evidence, validation and explanation.</p>}</div>
                </div>
              );
            }}
          </ModelGate>
          <ModelGate<Doc> kind="learning_metrics" m={ms.data.learning_metrics} label="Learning metrics">
            {(d, m) => <Metrics d={d} computed={<Computed m={m} />} />}
          </ModelGate>
        </>
      )}
      {drawer}
    </div>
  );
}

function CandidateDetail({ id, act }: { id: number; act: ReturnType<typeof useActs>[0] }) {
  const r = useEngineRead<Doc>("learn.show", { id }, `learning candidate ${id}`);
  return (
    <Card title={`Candidate #${id}`}>
      <ReadView r={r} label={`candidate ${id}`}>
        {(d) => {
          const c: Doc = d.candidate ?? {};
          const v: Doc | undefined = (d.validations ?? [])[(d.validations ?? []).length - 1];
          const explained = !!d.explanation?.text && d.explained_after_validation !== false;
          const st = String(d.state);
          return (
            <ApprovalPanel explanation={explained ? d.explanation.text : null}
              missing={v?.passed ? "Validated but not explained since: ask for the explanation before a partner decides." : "Not validated yet: validate it first, then ask for the explanation."}
              evidence={
                <div className="space-y-2 text-sm">
                  <p><span className="text-muted">What would change:</span> {typeof c.content?.text === "string" ? c.content.text : JSON.stringify(c.content)}</p>
                  <p><span className="text-muted">Why:</span> {c.rationale}</p>
                  <p><span className="text-muted">Expected effect:</span> {c.expected_effect} (metric {c.metric})</p>
                  <p className="text-xs text-muted">Evidence: {(c.evidence ?? []).length} observations — <span className="font-mono">{(c.evidence ?? []).join(", ")}</span></p>
                  {v && (
                    <div className="rounded-md border border-line p-2">
                      <p className="flex items-center gap-2"><StatusPill state={v.passed ? "passed" : "failed"} label={v.passed ? "Validation passed" : "Validation failed"} /><span className="text-xs text-muted">#{v.id} by {v.by}{v.agent ? ` (${v.agent})` : ""} · {fmtStamp(v.at)}</span></p>
                      <dl className="mt-1 grid grid-cols-2 gap-2 text-xs sm:grid-cols-3">
                        <div className="rounded bg-sunken p-1.5"><dt className="text-muted">Before</dt><dd className="break-all font-mono">{JSON.stringify(v.metrics?.before)}</dd></div>
                        <div className="rounded bg-sunken p-1.5"><dt className="text-muted">After</dt><dd className="break-all font-mono">{JSON.stringify(v.metrics?.after)}</dd></div>
                        <div className="rounded bg-sunken p-1.5"><dt className="text-muted">Regressions</dt><dd className="font-mono">{String(v.metrics?.regressions ?? "—")}</dd></div>
                      </dl>
                      <ul className="mt-1 flex flex-wrap gap-1.5 text-xs">
                        {Object.entries((v.layers ?? {}) as Record<string, Doc>).map(([k, l]) => <li key={k}><StatusPill state={l.passed ? "passed" : "failed"} label={`${k}${l.passed ? "" : `: ${(l.problems ?? []).join("; ")}`}`} /></li>)}
                      </ul>
                      {v.layers?.tests?.summary && <p className="mt-1 text-xs text-muted">Tests: {v.layers.tests.summary}</p>}
                    </div>
                  )}
                  {d.decision && <p className="text-xs">Decision: {d.decision.decision ?? ""} by {d.decision.by} {fmtStamp(d.decision.at)}{d.decision.reason ? ` — ${d.decision.reason}` : ""}</p>}
                  {d.applied && <p className="text-xs">Applied: version {d.applied.version ?? ""} {fmtStamp(d.applied.at)}</p>}
                </div>
              }
              decisions={
                <>
                  {!v?.passed && st !== "rejected" && <ActButton command="learn.validate" label="Validate" onClick={() => act({ command: "learn.validate", fixed: { id }, title: `Validate candidate ${id}` })} />}
                  {v?.passed && !explained && <ActButton command="learn.explain" label="Ask for the explanation" onClick={() => act({ command: "learn.explain", fixed: { id }, title: `Explain candidate ${id}` })} />}
                  {explained && st === "awaiting_approval" && <ActButton command="learn.approve" label="Approve…" tone="primary" onClick={() => act({ command: "learn.approve", fixed: { id }, title: `Approve learning ${id}` })} />}
                  {explained && st === "awaiting_approval" && <ActButton command="learn.reject" label="Reject…" tone="danger" onClick={() => act({ command: "learn.reject", fixed: { id }, title: `Reject learning ${id}` })} />}
                  {st === "approved" && <ActButton command="learn.apply" label="Apply" tone="primary" onClick={() => act({ command: "learn.apply", fixed: { id }, title: `Apply learning ${id}` })} />}
                  {st === "applied" && <ActButton command="learn.rollback" label="Roll back…" tone="danger" onClick={() => act({ command: "learn.rollback", fixed: { id }, title: `Roll back learning ${id}` })} />}
                  {!["approved", "applied", "rejected", "withdrawn"].includes(st) && <ActButton command="learn.withdraw" label="Withdraw…" onClick={() => act({ command: "learn.withdraw", fixed: { id }, title: `Withdraw candidate ${id}` })} />}
                </>
              } />
          );
        }}
      </ReadView>
    </Card>
  );
}

function Metrics({ d, computed }: { d: Doc; computed: React.ReactNode }) {
  const defs: Record<string, Doc> = d.definitions ?? {};
  const trend: Doc[] = d.trend ?? [];
  const firm: Record<string, Doc> = d.firm ?? {};
  const applied: Doc[] = d.applied ?? [];
  return (
    <Card title="Metrics" action={computed}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">Learning metrics: the firm's value and the trend by period</caption>
          <thead><tr className="border-b border-line text-left text-xs text-muted"><th scope="col" className="px-2 py-1">Metric</th><th scope="col" className="px-2 py-1">Better</th><th scope="col" className="px-2 py-1 text-right">Firm</th>
            {trend.map((t) => <th key={t.period} scope="col" className="px-2 py-1 text-right">{t.period}</th>)}</tr></thead>
          <tbody>
            {Object.entries(defs).map(([k, def]) => (
              <tr key={k} className="border-b border-line last:border-0">
                <th scope="row" className="px-2 py-1 text-left font-normal">{def.means}<span className="block font-mono text-[11px] text-muted">{k}</span></th>
                <td className="px-2 py-1 text-xs">{def.better}</td>
                <td className="px-2 py-1 text-right tabular-nums">{firm[k]?.value ?? "—"}{firm[k]?.denominator ? <span className="block text-[11px] text-muted">{firm[k].numerator}/{firm[k].denominator}</span> : null}</td>
                {trend.map((t) => <td key={t.period} className="px-2 py-1 text-right tabular-nums">{t[k] ?? "—"}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h3 className="mb-1 mt-4 text-xs font-semibold uppercase tracking-wide text-muted">Applied versions</h3>
      {applied.length === 0 ? <p className="text-sm text-muted">No learning applied yet.</p> : (
        <ul className="divide-y divide-line text-sm">{applied.map((a, i) => <li key={i} className="py-1.5">{a.scope ?? a.candidate} · version {a.version ?? "—"} · {fmtStamp(a.at ?? a.applied_at)}{a.before ? ` · before ${JSON.stringify(a.before)} → after ${JSON.stringify(a.after)}` : ""}</li>)}</ul>
      )}
    </Card>
  );
}
